import Search from '@monofront/design-system/visuals/Icons/Search';
import Modal from '@monofront/design-system/organisms/Modal';
import AutoComplete, {
  IAutoComplete,
} from '@monofront/design-system/components/Form/AutoComplete';
import ExpandPanel from '@monofront/design-system/components/ExpandPanel';
import HeartLoader from '@monofront/design-system/components/HeartLoader';
import InputIcon from '@monofront/design-system/components/Form/InputIcon';
import Button from '@monofront/design-system/components/Button';
import { SHOW_MORE_PICKUPS_STEP_SIZE } from '@monofront/business/constants';
import { useEffect, useRef, useState } from 'react';
import { daysOfWeek } from '@monofront/business/constants';
import { ISuggestedAddress, ShippingMethodTypes } from '@monofront/types';
import PickupMap from './PickupMap';
import { useSuggestedAddresses } from '@monofront/business/hooks';
import { useTranslations } from 'next-intl';
import { useCheckoutSelectAddressMachine } from '@context/checkoutCartMachineContext';

import {
  SELECT_PICKUP_POINT_TRANSITIONS,
  SELECT_PICKUP_POINT_STATES,
} from '@monofront/business/stateMachines';
import useI18n from '@hooks/i18n/useI18n';
import getConfig from 'next/config';
import { v4 as uuid } from 'uuid';
import { DeliveryBarMachineContext } from '@monofront/business/contexts';
import { EVENTS } from '@monofront/business/stateMachines/deliveryBar';
import { IconSizes } from '@monofront/design-system/visuals/Icons/type';

const AddressAutoComplete = AutoComplete as IAutoComplete<ISuggestedAddress>;

const {
  publicRuntimeConfig: { mapboxToken },
} = getConfig();

const sessionToken = uuid();

interface ISelectPickupProps {
  handleClose: () => void;
  isOpen: boolean;
}

const SelectPickup: React.FC<ISelectPickupProps> = ({
  handleClose,
  isOpen,
}) => {
  const { selectPickupPointState, sendToSelectPickupPoint } =
    useCheckoutSelectAddressMachine();

  const [pickupFocused, setPickupFocused] = useState<string | null>(null);
  const [displayedPickupsCount, setDisplayedPickupsCount] = useState<number>(
    SHOW_MORE_PICKUPS_STEP_SIZE,
  );
  const { locale, availableCountryLocale } = useI18n();

  const pickupElements = useRef({});
  const {
    writtenStreet,
    setWrittenStreet,
    suggestedAddresses,
    isSuggestedAddressLoading,
  } = useSuggestedAddresses({
    mapboxToken,
    locale,
    sessionToken,
    searchableCountry: availableCountryLocale,
  });

  const { send: sendToDeliveryBarMachine } =
    DeliveryBarMachineContext.useActorRef();

  const {
    address: defaultAddress,
    shippingMethods,
    pickupPoints,
    homePosition,
  } = selectPickupPointState.context;

  const seeMoreCount = Math.min(
    pickupPoints.length - displayedPickupsCount,
    SHOW_MORE_PICKUPS_STEP_SIZE,
  );
  const displayedPickupList = pickupPoints.slice(0, displayedPickupsCount);

  const tShipping = useTranslations('Shipping');
  const tGlobal = useTranslations('Global');

  const formatDistanceFromOrigin = (distance: string) => {
    if (!distance.includes('.')) {
      return distance + ',0 km';
    }

    return distance.replace('.', ',') + ' km';
  };

  useEffect(() => {
    if (pickupFocused) {
      pickupElements.current?.[pickupFocused]?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [pickupFocused]);

  return (
    <>
      <Modal
        UNSAFE_className="select-pickup-modal"
        externalToggle={handleClose}
        isOpen={isOpen}
        isClosable
      >
        <h2 className="title">{tShipping('chooseRelayPoint')}</h2>

        <div className="container">
          <div className="search-bar">
            {defaultAddress!.countryCode === 'FR' && (
              <AddressAutoComplete
                isLoading={isSuggestedAddressLoading}
                options={
                  writtenStreet?.length
                    ? suggestedAddresses?.map((address) => ({
                        id: address.label,
                        label: address.label,
                        value: address,
                      }))
                    : undefined
                }
                handleSelect={(value) => {
                  setWrittenStreet(value.label);
                  sendToSelectPickupPoint({
                    type: SELECT_PICKUP_POINT_TRANSITIONS.FETCH_POINTS,
                    address: value,
                  });
                }}
                renderInput={(params) => (
                  <InputIcon
                    {...params}
                    rounded
                    placeholder={`${defaultAddress!.street} ${
                      defaultAddress!.postcode
                    } ${defaultAddress!.city}`}
                    type="search"
                    value={writtenStreet}
                    onChange={async (event) => {
                      setWrittenStreet(event.target.value);
                    }}
                  >
                    <Search size={IconSizes.md} />
                  </InputIcon>
                )}
              />
            )}
          </div>
          {selectPickupPointState.matches({
            [SELECT_PICKUP_POINT_STATES.OPENED]:
              SELECT_PICKUP_POINT_STATES.POINTS_FETCHING,
          }) ? (
            <HeartLoader />
          ) : selectPickupPointState.matches(
              SELECT_PICKUP_POINT_STATES.GET_ERROR,
            ) || !(pickupPoints?.length > 0) ? (
            <div className="error-no-pickup-found">
              {tShipping('noRelayFound')}
            </div>
          ) : (
            <>
              <div className="map">
                {isOpen && (
                  <PickupMap
                    pickupList={displayedPickupList}
                    pickupFocused={pickupFocused}
                    setPickupFocused={setPickupFocused}
                    homePosition={homePosition}
                  />
                )}
              </div>
              <div className="drop-off-list-content">
                <ol>
                  {selectPickupPointState.matches({
                    [SELECT_PICKUP_POINT_STATES.OPENED]:
                      SELECT_PICKUP_POINT_STATES.POINTS_FETCHING,
                  }) && <HeartLoader />}
                  {displayedPickupList.map((pickupPoint) => {
                    const {
                      dayOfWeek,
                      pointId,
                      pointName,
                      street,
                      zip,
                      city,
                      courier,
                      distanceFromOrigin,
                    } = pickupPoint;
                    const isFocused = pickupFocused === pointId;

                    return (
                      <li
                        key={pointId}
                        className={isFocused ? 'focused' : ''}
                        onClick={() => setPickupFocused(pointId)}
                        ref={(markerRef) => {
                          pickupElements.current[pointId] = markerRef;
                        }}
                      >
                        <div className="drop-off-address">
                          <div className="heading">
                            <div className="w-full">
                              <div className="flex justify-between w-full">
                                <span className="capitalize hxb">
                                  {pointName.toLowerCase()}
                                </span>
                                <span className="hxrt distance-from-origin">
                                  {formatDistanceFromOrigin(
                                    distanceFromOrigin.value,
                                  )}
                                </span>
                              </div>
                              <div>
                                <div className="capitalize hxrt">
                                  {street.toLowerCase()}
                                </div>
                                <div className="capitalize hxrt">
                                  {zip} {city.toLowerCase()}
                                </div>
                              </div>
                            </div>
                          </div>
                          <ExpandPanel.Details isInline>
                            <ExpandPanel.Summary>
                              <div className="summary-container">
                                <div className="summary-title">
                                  <span className="summary">
                                    {tShipping('timeSlots')}
                                  </span>
                                  <ExpandPanel.ExpandIcon />
                                </div>
                                <Button
                                  dataTestId="SelectPickup-select-pickup-location"
                                  disabled={dayOfWeek.length <= 0}
                                  block={false}
                                  hasMinWidth={false}
                                  onClick={() => {
                                    const courierCode =
                                      shippingMethods.find(
                                        ({ tmsCode }) => tmsCode === courier,
                                      )?.code || courier;

                                    sendToSelectPickupPoint({
                                      type: SELECT_PICKUP_POINT_TRANSITIONS.SELECT_POINT,
                                      shipmentMethodCode: courierCode,
                                      pickupAddress: {
                                        firstName: '',
                                        lastName: '',
                                        phoneNumber: '',
                                        company: pickupPoint.pointName,
                                        countryCode:
                                          defaultAddress!.countryCode,
                                        provinceCode: '',
                                        provinceName: '',
                                        street: pickupPoint.street,
                                        city: pickupPoint.city,
                                        postcode: pickupPoint.zip,
                                      },
                                      pickupPointSelected: pickupPoint,
                                    });
                                    sendToDeliveryBarMachine({
                                      type: EVENTS.OVERWRITE_SHIPPING_METHOD,
                                      shippingMethod: {
                                        type: ShippingMethodTypes.PICKUP,
                                        pickupPointId: pickupPoint.pointId,
                                        pickupPointLabel: pickupPoint.pointName,
                                        pickupPointCity: pickupPoint.city,
                                        pickupPointStreet: pickupPoint.street,
                                        pickupPointZipCode: pickupPoint.zip,
                                      },
                                    });
                                  }}
                                >
                                  {tShipping('choose')}
                                </Button>
                              </div>
                            </ExpandPanel.Summary>
                            <table className="days">
                              <tbody>
                                {dayOfWeek.map(({ day, hours }) => (
                                  <tr key={day}>
                                    <td>{daysOfWeek[day]}</td>
                                    {hours.map((hour) => (
                                      <td key={hour}>{hour}</td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </ExpandPanel.Details>
                        </div>
                      </li>
                    );
                  })}
                </ol>
                {seeMoreCount > 0 && (
                  <Button
                    onClick={() =>
                      setDisplayedPickupsCount(
                        (prev) => prev + SHOW_MORE_PICKUPS_STEP_SIZE,
                      )
                    }
                    block={false}
                    isLoading={false}
                    className="btn-see-more"
                    hasMinWidth={false}
                  >
                    {`${tGlobal('seeMore')} (${seeMoreCount})`}
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      </Modal>
      <style jsx>{`
        .summary-container {
          display: flex;
          justify-content: space-between;
          align-items: center;
          width: 100%;
        }

        .summary-title {
          width: 100px;
        }

        .flex {
          display: flex;
        }

        .justify-between {
          justify-content: space-between;
        }

        .w-full {
          width: 100%;
        }

        .search-bar {
          margin: var(--space-2) var(--space-4);
        }

        .hxrt {
          color: var(--clr-grey-1);
        }

        .drop-off-address {
          width: 100%;
        }

        .heading {
          display: flex;
        }

        .heading,
        .heading div {
          margin-bottom: var(--space-1);
        }

        .title {
          font-size: var(--font-size-2);
          text-align: center;
          margin: var(--space-6) var(--space-3) var(--space-4);
        }

        ol {
          padding: 0 var(--space-2) var(--space-2) 0;
        }

        ol,
        .map {
          list-style: none;
          counter-reset: drop-off-counter;
        }

        ol li {
          display: flex;
          padding: var(--space-4);
          cursor: pointer;
          margin-bottom: var(--space-2);
          border: 1px solid var(--clr-tertiary-2);
          border-radius: var(--rd-2);
        }

        ol li,
        :global(.marker-icon) {
          counter-increment: drop-off-counter;
        }

        .heading::before {
          --size: 32px;
          flex-shrink: 0;
        }

        :global(.marker-icon)::before {
          --size: 28px;
          border: 2px solid var(--white);
        }

        .heading::before,
        :global(.marker-icon)::before {
          display: block;
          content: counter(drop-off-counter);
          font-weight: bold;
          line-height: var(--size);
          width: var(--size);
          height: var(--size);
          background-color: var(--clr-primary-1);
          border-radius: 50%;
          text-align: center;
          margin-right: var(--space-4);
        }

        ol li.focused {
          background-color: var(--clr-primary-5);
        }

        ol li.focused,
        :global(.marker-icon.focused) {
          color: var(--black) !important;
        }

        ol li.focused::before,
        :global(.marker-icon.focused)::before {
          background-color: #000;
          color: var(--white);
        }

        table.days {
          width: 100%;
          border-spacing: 0 var(--space-2);
        }

        td:not(:first-child) {
          text-align: right;
          font-weight: bold;
        }

        .error-no-pickup-found {
          grid-row: 2;
          grid-column: 1;
          margin: var(--space-2) var(--space-4);
          color: var(--clr-danger);
        }

        .summary {
          font-weight: var(--font-weight-bold);
        }

        :global(.btn-see-more) {
          margin: 0 auto;
        }

        :global(.btn-see-more) {
          margin: var(--space-4) auto var(--space-2);
        }

        .distance-from-origin {
          margin-left: var(--space-2);
        }

        @media (max-width: 800px) {
          :global(.select-pickup-modal) {
            top: 0;
            bottom: 0;
            left: 0;
            right: 0;
            overflow: auto;
          }

          ol {
            padding: var(--space-2);
          }

          .map {
            width: 100%;
            height: 90vw;
            max-height: 40vh;
            position: sticky;
            top: 0;
            z-index: 1;
          }

          .drop-off-list-content li {
            scroll-margin-top: 40vh;
          }

          :global(.btn-see-more) {
            margin: 0 auto var(--space-2);
          }
        }

        @media (min-width: 801px) {
          :global(.select-pickup-modal) {
            top: 10%;
            overflow: hidden;
            width: 90vw;
            max-width: 996px;
            height: 626px;
          }

          .container {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            row-gap: var(--space-4);
            column-gap: var(--space-4);
            grid-template-rows: auto 1fr;
            padding: 0 var(--space-5) var(--space-5);
          }

          .search-bar {
            grid-row: 1;
            grid-column: 1;
            margin: 0;
          }

          .map {
            grid-column: 1;
            width: calc(90vw - 375px);
            max-width: 569px;
            height: 100%;
          }

          .drop-off-list-content {
            overflow-y: auto;
            height: 517px;
            grid-row: 1 / 3;
            grid-column: 2;
          }
        }
      `}</style>
    </>
  );
};

export default SelectPickup;
